// =============================================================================
// SIREPPAR - Panel de informes y resoluciones del expediente
// =============================================================================
// Muestra los documentos VIGENTES de la vía Secretario → TEP según la fase, para
// que la supervisora y el Secretario los tengan a mano sin ir a /documentos.
// Vive en la tab "Informes", que se abre sola cuando la pelota es del Secretario.
//
// DOS MODOS:
//   - RECONOCIMIENTO (`fase` presente): vista por fase, según la regla de abajo.
//     Muestra solo lo vigente de la etapa + las actas aprobatorias ya obtenidas.
//   - RESTO DE LOS TRÁMITES (`fase` null: reempadronamiento, cambio de
//     autoridades, no presentado, etc.): historial completo en orden cronológico
//     descendente. Estos trámites no tienen fases con etapas acumulativas, así
//     que lo útil es el legajo entero de la vía Secretario → TEP.
//
// REGLA (la misma en todas las etapas del trámite):
//   EN_SECRETARIO  → Informe al Secretario        + las actas aprobatorias anteriores
//   EN_TEP         → Informe al TEP (reemplaza al del Secretario) + actas anteriores
//   APROBADA       → la resolución aprobatoria    + las actas aprobatorias anteriores
//   RECHAZADA      → la resolución de rechazo + el Informe al TEP (para comparar)
//   vuelta a PEND. → Informe al TEP anterior + acta rechazada (para comparar)
//
// Las actas APROBATORIAS de etapas ya cerradas quedan FIJAS de acá en adelante: son
// el respaldo de lo que el partido ya obtuvo. Antes el switch no tenía casos para
// el tramo del provisorio y caía en `default`, así que al otorgarse el provisorio
// las resoluciones aprobadas de atributos y documentación desaparecían de la vista.
//
// La DEVOLUCIÓN del Secretario a la SEP se muestra en la fase a la que devolvió, y
// desaparece sola al avanzar: el panel se arma por fase, no acumula historial.
// =============================================================================

import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import type { FaseReconocimiento } from '../../../utils/fasesReconocimiento';

interface DocumentoMinimo {
  id: string;
  tipo: string;
  titulo?: string;
  creadoEn: string;
}

type Tono = 'indigo' | 'blue' | 'red' | 'green' | 'amber';

const TONOS: Record<Tono, { box: string; label: string; btn: string }> = {
  indigo: { box: 'bg-indigo-50 border-indigo-200', label: 'text-indigo-700', btn: 'bg-indigo-600 hover:bg-indigo-700' },
  blue: { box: 'bg-blue-50 border-blue-200', label: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' },
  red: { box: 'bg-red-50 border-red-200', label: 'text-red-700', btn: 'bg-red-600 hover:bg-red-700' },
  green: { box: 'bg-green-50 border-green-200', label: 'text-green-700', btn: 'bg-green-600 hover:bg-green-700' },
  amber: { box: 'bg-amber-50 border-amber-200', label: 'text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700' },
};

// Informes de circulación interna: los tres de la vía SEP → Secretario → TEP. El
// backend ya no se los manda al partido (los marca `esInterno` y los filtra al
// consultar el expediente), pero se filtran también acá: si mañana llegara uno por
// otro camino, no tiene que aparecerle al apoderado.
// La RESOLUCION_TEP NO está en la lista: el partido tiene que verla.
const TIPOS_SOLO_INTERNOS = [
  'INFORME_SECRETARIO',
  'INFORME_TEP',
  'INFORME_DEVOLUCION_SECRETARIO',
  // Elevación de la SEP al Secretario en la constitución de alianza. Mismo criterio
  // que INFORME_SECRETARIO: no reconoce ni rechaza nada, es la vía interna.
  // La RESOLUCION_RECONOCIMIENTO_ALIANZA no está: es el acto que resuelve.
  'INFORME_ELEVACION_ALIANZA',
];

export default function PanelInformes({
  documentos,
  fase,
  esUsuarioInterno = true,
}: {
  documentos?: DocumentoMinimo[];
  fase: FaseReconocimiento | null;
  esUsuarioInterno?: boolean;
}) {
  // `etiqueta` distingue la etapa por el título del documento: '(Atributos)',
  // '(Documentación)' o '(Autoridades definitivas)'.
  const ultimo = (tipo: string, etiqueta?: string) =>
    [...(documentos ?? [])]
      .filter((d) => d.tipo === tipo && (!etiqueta || (d.titulo || '').includes(etiqueta)))
      .sort((a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime())[0];

  // El N° de acta que trae el título ("… — ACTA N°001/2026 (2)"). Sirve para agrupar
  // los archivos de UNA MISMA resolución: el TEP puede resolver en más de un
  // instrumento y el backend guarda un Documento por archivo.
  const nroActaDe = (titulo?: string) => titulo?.match(/ACTA\s*N°\s*\d+\/\d{4}/i)?.[0] ?? null;
  // El sufijo de numeración que el backend agregó cuando hay más de un archivo.
  const sufijoDe = (titulo?: string) => titulo?.match(/\((\d+)\)\s*$/)?.[0] ?? '';

  /**
   * TODOS los archivos de la resolución más reciente de una etapa. Antes acá se usaba
   * `ultimo()` y con varios archivos se veía solo uno: el resto quedaba únicamente en
   * la pestaña Documentos.
   */
  const ultimaActa = (etiqueta: string): DocumentoMinimo[] => {
    const dela = [...(documentos ?? [])]
      .filter((d) => d.tipo === 'RESOLUCION_TEP' && (d.titulo || '').includes(etiqueta))
      .sort((a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime());
    if (dela.length === 0) return [];
    const nro = nroActaDe(dela[0].titulo);
    // Sin N° de acta reconocible (datos viejos) se muestra solo el más reciente.
    if (!nro) return [dela[0]];
    return dela
      .filter((d) => nroActaDe(d.titulo) === nro)
      .sort((a, b) => (sufijoDe(a.titulo) || '(1)').localeCompare(sufijoDe(b.titulo) || '(1)'));
  };

  const cards: Array<{ doc: DocumentoMinimo; label: string; tono: Tono; btn: string }> = [];
  const push = (
    doc: DocumentoMinimo | DocumentoMinimo[] | undefined,
    label: string,
    tono: Tono,
    btn: string,
  ) => {
    if (!doc) return;
    // Varios archivos del mismo acto: una card por archivo, con el (1)/(2) del título
    // pegado al label para que se distingan.
    const docs = Array.isArray(doc) ? doc : [doc];
    for (const d of docs) {
      if (!esUsuarioInterno && TIPOS_SOLO_INTERNOS.includes(d.tipo)) continue;
      const sufijo = docs.length > 1 ? ` ${sufijoDe(d.titulo) || ''}`.trimEnd() : '';
      cards.push({ doc: d, label: `${label}${sufijo}`, tono, btn });
    }
  };

  // En fases de ATRIBUTOS, una "Resolución TEP (Atributos)" solo puede ser un
  // RECHAZO (si aprobaba, la fase ya sería DOCUMENTACIÓN_*). En fases de
  // DOCUMENTACIÓN en adelante, la de atributos es la APROBATORIA (queda fija).
  const actaAtributos = ultimaActa('(Atributos)');
  const actaDoc = ultimaActa('(Documentación)');
  const actaDefinitivo = ultimaActa('(Autoridades definitivas)');
  const devolucion = ultimo('INFORME_DEVOLUCION_SECRETARIO');

  // Las dos actas aprobatorias que el partido ya tiene: acompañan a todo el tramo
  // posterior al provisorio.
  const pushActasAprobadas = () => {
    push(actaDoc, 'Resolución del TEP — Documentación aprobada (provisorio)', 'green', 'Ver resolución');
    push(actaAtributos, 'Resolución del TEP — Atributos aprobados', 'green', 'Ver resolución');
  };

  // ---- MODO HISTORIAL (todo trámite que no sea reconocimiento) ----------------
  // Se listan TODOS los informes/resoluciones de la vía Secretario → TEP, del más
  // nuevo al más viejo. No se filtra por estado: el circuito puede ir y volver
  // (devolución del Secretario, intimación, nueva elevación) y cada pieza es
  // respaldo del expediente.
  const HISTORIAL: Array<{ tipo: string; label: string; tono: Tono; btn: string }> = [
    { tipo: 'INFORME_SECRETARIO', label: 'Informe de la SEP al Secretario', tono: 'indigo', btn: 'Ver informe' },
    { tipo: 'INFORME_TEP', label: 'Informe del Secretario elevado al TEP', tono: 'blue', btn: 'Ver informe' },
    { tipo: 'INFORME_DEVOLUCION_SECRETARIO', label: 'Devolución del Secretario a la SEP', tono: 'amber', btn: 'Ver informe' },
    { tipo: 'RESOLUCION_TEP', label: 'Resolución del TEP', tono: 'green', btn: 'Ver resolución' },
    // Reempadronamiento: cuando el expediente está correcto lo resuelve el
    // Secretario Electoral por sí mismo, sin elevarlo al Tribunal.
    { tipo: 'RESOLUCION_SECRETARIO', label: 'Resolución del Secretario Electoral', tono: 'green', btn: 'Ver resolución' },
    // Constitución de alianza: la Dirección eleva al Secretario, el Secretario eleva al
    // TEP y el TEP resuelve. El acta del TEP se guarda con el tipo
    // RESOLUCION_RECONOCIMIENTO_ALIANZA —y no RESOLUCION_TEP— porque es la clave con la
    // que el banner externo arma la descarga de la resolución; el órgano lo lleva el label.
    { tipo: 'INFORME_ELEVACION_ALIANZA', label: 'Informe de elevación al Secretario (Alianza)', tono: 'indigo', btn: 'Ver informe' },
    { tipo: 'RESOLUCION_RECONOCIMIENTO_ALIANZA', label: 'Resolución del TEP (Alianza)', tono: 'amber', btn: 'Ver resolución' },
  ];

  if (!fase) {
    const porTipo = new Map(HISTORIAL.map((h) => [h.tipo, h]));
    [...(documentos ?? [])]
      .filter((d) => porTipo.has(d.tipo))
      .sort((a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime())
      .forEach((doc) => {
        const h = porTipo.get(doc.tipo)!;
        // Varios archivos de la misma resolución: el título trae el (1)/(2).
        const sufijo = sufijoDe(doc.titulo);
        push(doc, sufijo ? `${h.label} ${sufijo}` : h.label, h.tono, h.btn);
      });
  }

  // El informe de apertura del expediente acompaña TODA la etapa de atributos: es el
  // acto que la abre, y se consulta para verificar quién presentó el trámite.
  const informeInicio = ultimo('INFORME_INICIO_TRAMITE');

  switch (fase) {
    // ---- ATRIBUTOS ----
    case 'ATRIBUTOS_PENDIENTES':
      // Tras un rechazo: último informe al TEP + acta rechazada, para comparar.
      push(informeInicio, 'Informe de inicio de trámite', 'green', 'Ver informe');
      push(ultimo('INFORME_TEP', '(Atributos)'), 'Informe elevado al TEP (anterior)', 'blue', 'Ver informe');
      push(actaAtributos, 'Resolución del TEP — Atributos rechazados (para comparar)', 'red', 'Ver resolución');
      break;
    // Verificados y elevados: el Secretario tiene que ver el informe de la SEP que le
    // pide publicar (es lo que va a firmar como edicto).
    case 'ATRIBUTOS_A_PUBLICAR':
      push(
        ultimo('INFORME_SECRETARIO', '(Atributos a publicar)'),
        'Informe de la SEP — atributos a publicar',
        'indigo',
        'Ver informe',
      );
      push(informeInicio, 'Informe de inicio de trámite', 'green', 'Ver informe');
      push(actaAtributos, 'Resolución del TEP — Atributos rechazados (anterior)', 'red', 'Ver resolución');
      break;
    case 'ATRIBUTOS_PUBLICADOS':
      // Publicados (aún sin informe al Secretario): si viene de un rechazo, se
      // muestra lo anterior para comparar.
      push(ultimo('PUBLICACION_ATRIBUTOS'), 'Publicación de atributos (Art. 10)', 'blue', 'Ver publicación');
      push(ultimo('INFORME_TEP', '(Atributos)'), 'Informe elevado al TEP (anterior)', 'blue', 'Ver informe');
      push(actaAtributos, 'Resolución del TEP — Atributos rechazados (anterior)', 'red', 'Ver resolución');
      break;
    case 'ATRIBUTOS_EN_SECRETARIO':
      push(ultimo('INFORME_SECRETARIO', '(Atributos)'), 'Informe al Secretario', 'indigo', 'Ver informe');
      push(actaAtributos, 'Resolución del TEP — Atributos rechazados (anterior)', 'red', 'Ver resolución');
      break;
    case 'ATRIBUTOS_EN_TEP':
      push(ultimo('INFORME_TEP', '(Atributos)'), 'Informe elevado al TEP', 'blue', 'Ver informe');
      break;
    case 'ATRIBUTOS_RECHAZADOS':
      push(actaAtributos, 'Resolución del TEP — Atributos rechazados', 'red', 'Ver resolución');
      break;

    // ---- DOCUMENTACIÓN (sobre el acta "Atributos aprobados", que queda fija) ----
    case 'DOCUMENTACION_PENDIENTE':
      push(ultimo('INFORME_TEP', '(Documentación)'), 'Informe elevado al TEP (anterior)', 'blue', 'Ver informe');
      push(actaDoc, 'Resolución del TEP — Documentación rechazada (para comparar)', 'red', 'Ver resolución');
      push(actaAtributos, 'Resolución del TEP — Atributos aprobados', 'green', 'Ver resolución');
      break;
    case 'DOCUMENTACION_EN_SECRETARIO':
      push(ultimo('INFORME_SECRETARIO', '(Documentación)'), 'Informe al Secretario (Documentación)', 'indigo', 'Ver informe');
      push(actaDoc, 'Resolución del TEP — Documentación rechazada (para comparar)', 'red', 'Ver resolución');
      push(actaAtributos, 'Resolución del TEP — Atributos aprobados', 'green', 'Ver resolución');
      break;
    case 'DOCUMENTACION_EN_TEP':
      push(ultimo('INFORME_TEP', '(Documentación)'), 'Informe elevado al TEP (Documentación)', 'blue', 'Ver informe');
      push(actaAtributos, 'Resolución del TEP — Atributos aprobados', 'green', 'Ver resolución');
      break;
    case 'DOCUMENTACION_RECHAZADA':
      push(actaDoc, 'Resolución del TEP — Documentación rechazada', 'red', 'Ver resolución');
      push(actaAtributos, 'Resolución del TEP — Atributos aprobados', 'green', 'Ver resolución');
      break;

    // ---- TRAMO DEL PROVISORIO (afiliados → autoridades → definitivo) ----
    // Acá el partido ya tiene el provisorio: las dos actas aprobatorias quedan
    // fijas en todas las fases.
    case 'DOCUMENTACION_APROBADA':
    case 'AFILIADOS_PENDIENTES':
    case 'CARGA_AFILIADOS_EN_CURSO':
    case 'RECONOCIMIENTO_PROVISORIO':
      pushActasAprobadas();
      break;
    case 'AUTORIDADES_PENDIENTES':
      // Si el Secretario devolvió las autoridades, la devolución se ve acá: es la
      // fase a la que volvió el expediente.
      push(devolucion, 'Devolución del Secretario a la SEP', 'amber', 'Ver informe');
      pushActasAprobadas();
      break;
    case 'DEFINITIVO_EN_SECRETARIO':
      push(ultimo('INFORME_SECRETARIO', '(Autoridades definitivas)'), 'Informe al Secretario (Autoridades definitivas)', 'indigo', 'Ver informe');
      push(devolucion, 'Devolución del Secretario a la SEP', 'amber', 'Ver informe');
      pushActasAprobadas();
      break;
    case 'DEFINITIVO_EN_TEP':
      push(ultimo('INFORME_TEP', '(Autoridades definitivas)'), 'Informe elevado al TEP (Autoridades definitivas)', 'blue', 'Ver informe');
      pushActasAprobadas();
      break;
    case 'DEFINITIVO_RECHAZADO':
      push(actaDefinitivo, 'Resolución del TEP — Reconocimiento definitivo rechazado', 'red', 'Ver resolución');
      push(ultimo('INFORME_TEP', '(Autoridades definitivas)'), 'Informe elevado al TEP (para comparar)', 'blue', 'Ver informe');
      pushActasAprobadas();
      break;
    case 'RECONOCIMIENTO_DEFINITIVO':
      push(actaDefinitivo, 'Resolución del TEP — Reconocimiento definitivo otorgado', 'green', 'Ver resolución');
      pushActasAprobadas();
      break;
    default:
      break;
  }

  // La devolución en las fases de atributos/documentación: se agrega al final si no
  // la puso ya el case (para no depender de repetir el push en cada rama).
  if (devolucion && esUsuarioInterno && !cards.some((c) => c.doc.id === devolucion.id)) {
    const enFaseDevuelta = fase === 'ATRIBUTOS_PUBLICADOS' || fase === 'DOCUMENTACION_PENDIENTE'
      || fase === 'ATRIBUTOS_EN_SECRETARIO' || fase === 'DOCUMENTACION_EN_SECRETARIO';
    if (enFaseDevuelta) {
      cards.unshift({ doc: devolucion, label: 'Devolución del Secretario a la SEP', tono: 'amber', btn: 'Ver informe' });
    }
  }

  const abrir = async (docId: string) => {
    try {
      const response = await api.get(`/documentos/${docId}/url`);
      const url = response.data.data?.url || response.data.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('No se pudo abrir el documento');
    }
  };

  if (cards.length === 0) {
    return (
      <p className="text-center text-sm text-gray-400 py-8">
        {fase
          ? 'Todavía no hay informes ni resoluciones en esta etapa del trámite.'
          : 'Todavía no hay informes ni resoluciones en este expediente.'}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {cards.map((c) => {
        const t = TONOS[c.tono];
        return (
          <div key={c.doc.id} className={`${t.box} border rounded-lg p-4 flex items-center justify-between gap-4`}>
            <div>
              <p className={`text-xs font-bold ${t.label} uppercase tracking-wide mb-1`}>{c.label}</p>
              <p className="text-sm text-gray-800">{c.doc.titulo}</p>
              {/* En el historial (trámites sin fases) la fecha ordena la lectura:
                  puede haber varias vueltas del mismo tipo de informe. */}
              {!fase && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(c.doc.creadoEn).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
            <button
              onClick={() => abrir(c.doc.id)}
              className={`px-4 py-2 ${t.btn} text-white text-sm font-medium rounded-lg whitespace-nowrap`}
            >
              📄 {c.btn}
            </button>
          </div>
        );
      })}
    </div>
  );
}
